'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { MessageBubble, ChatMessage, ChatInputArea, ChatInputAreaHandle } from '@contentgrowth/llm-service/ui/react';
import { useChat } from '@/hooks/useChat';
import { ExclamationCircleIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { apiClient } from '@/app/api/client';

// Helper to extract article content from delimiter blocks
function extractArticleContent(text: string): string | null {
  const startMarker = '---ARTICLE_CONTENT---';
  const endMarker = '---END_ARTICLE_CONTENT---';
  const startIdx = text.indexOf(startMarker);
  const endIdx = text.indexOf(endMarker);

  if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
    return text.substring(startIdx + startMarker.length, endIdx).trim();
  }
  return null;
}

interface AIChatModeProps {
  onComplete: (data: any) => void;
}

const AIChatMode: React.FC<AIChatModeProps> = ({ onComplete }) => {
  const [isComplete, setIsComplete] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [initError, setInitError] = useState<{ message: string; retryable: boolean } | null>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<ChatInputAreaHandle>(null);

  const handleApiResponse = (data: any) => {
    // Handle Assistant Hub response format
    // Backend returns: { type: 'action', data: { action: {...}, message: '...' } } 
    // or { type: 'chat', data: 'message' }

    // Handle action response: { type: 'action', data: { action: { type: 'create_article', payload: {...} } } }
    if (data.type === 'action' && data.data?.action?.type === 'create_article') {
      const actionData = data.data;
      const payload = actionData.action.payload;

      const articleData = {
        title: payload.title,
        category: payload.category,
        tags: payload.tags || [],
        content: payload.content || '',
      };

      toast.success(actionData.message || 'Great! I\'ve drafted the article for you.');
      setIsComplete(true);
      onComplete(articleData);
      return null;
    }

    // Handle standard chat response: { type: 'chat', data: 'message' }
    if (data.type === 'chat' && data.data) {
      return data.data;
    }

    // Handle direct message format: { message: '...' }
    if (data.message) {
      return data.message;
    }

    // Handle legacy format for backwards compatibility
    if (data.type === 'article') {
      const { message, ...articleData } = data.payload;
      toast.success(message || 'Great! I\'ve drafted the article for you.');
      setIsComplete(true);
      onComplete(articleData);
      return null;
    } else if (data.type === 'message') {
      return data.payload?.content || data.data;
    }

    // Fallback for unexpected structure
    console.error('Unexpected AI response structure:', data);
    return data.data || data.message || 'Sorry, I received a response I could not understand.';
  };

  const {
    messages,
    setMessages,
    input,
    handleInputChange,
    handleSubmit,
    isLoading,
    error,
    stop
  } = useChat({
    apiEndpoint: '/api/assistant/chat',
    onResponse: handleApiResponse,
  });

  // Initialize session with /api/assistant/start to get contextual welcome
  const initSession = useCallback(async () => {
    setIsInitializing(true);
    setInitError(null);

    try {
      const response = await apiClient.post('/api/assistant/start', {});
      const data = response.data;

      if (data.messages && data.messages.length > 0) {
        setMessages(data.messages);
      } else {
        // Server responded but with no messages - use fallback
        setMessages([{
          id: 'initial-greeting',
          role: 'assistant',
          content: "Hello! I'm here to help you create a new article. What topic would you like to write about?",
        }]);
      }
    } catch (error: any) {
      console.error('Failed to initialize assistant session:', error);

      // Extract error details from response
      const errorData = error.response?.data;
      const errorMessage = errorData?.message || error.message || 'Failed to connect to AI service.';
      const retryable = errorData?.retryable !== false; // Default to retryable

      setInitError({ message: errorMessage, retryable });
    } finally {
      setIsInitializing(false);
    }
  }, [setMessages]);

  useEffect(() => {
    initSession();
  }, [initSession]);

  useEffect(() => {
    if (!isInitializing && !initError) {
      inputRef.current?.focus();
    }
  }, [isInitializing, initError]);

  // Auto-scroll to the latest message
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  // Show loading state while initializing
  if (isInitializing) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <p className="mt-4 text-gray-500">Initializing assistant...</p>
      </div>
    );
  }

  // Show error state with retry button
  if (initError) {
    return (
      <div className="flex flex-col h-full items-center justify-center p-6">
        <ExclamationCircleIcon className="h-12 w-12 text-red-500 mb-4" />
        <p className="text-red-600 text-center mb-4">{initError.message}</p>
        {initError.retryable && (
          <button
            onClick={initSession}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
          >
            <ArrowPathIcon className="h-5 w-5" />
            Retry Connect
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Chat Messages Area */}
      <div ref={chatContainerRef} className="flex-grow p-6 overflow-y-auto">
        {messages.map((msg) => (
          <MessageBubble key={msg.id} message={msg} isUser={msg.role === 'user'} />
        ))}
        {isLoading && (
          <MessageBubble
            message={{ id: 'loading', role: 'assistant', content: 'Thinking...' }}
            isUser={false}
          />
        )}
      </div>

      {/* Chat Input Area */}
      <div className="p-4 border-t">
        {error && (
          <div className="mb-2 flex items-center text-sm text-red-600">
            <ExclamationCircleIcon className="h-5 w-5 mr-2" />
            <span>{error.message}</span>
          </div>
        )}
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(e); }}>
          <ChatInputArea
            ref={inputRef}
            value={input}
            onChange={handleInputChange}
            onSubmit={(msg) => handleSubmit({ preventDefault: () => { } } as React.FormEvent)}
            isSending={isLoading}
            onStop={stop}
            showInputForm={!isComplete}
            currentTask={null} // Required prop
            placeholder="Tell me about the article you want to create..."
          />
        </form>
      </div>
    </div>
  );
};

export default AIChatMode;
